import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { LoadingSpinner } from '../../common';
import { usePermission } from '../../../hooks/usePermission';
import { EdiService, formatCents, formatEdiDate, projectedChargeText } from '../../../services/billing/edi.service';
import type { EdiBatchDetail } from '../../../dtos/billing/edi.dto';
import { BILLING_SUBMIT, Cell, ErrorNote, errorText } from './ediUi';

interface BatchDetailProps {
  batchId: string;
  onBack: () => void;
}

const SENDABLE = new Set(['AWAITING_SEND', 'PARTIAL', 'HELD']);

/** "N sent, M held (blocked)": refused claims are the ones the send-time gate kept back. */
export function sendNotice(selectedCount: number, refusedCount: number): string {
  const sent = Math.max(0, selectedCount - refusedCount);
  const head = `${sent} ${sent === 1 ? 'claim' : 'claims'} sent`;
  return refusedCount > 0 ? `${head}, ${refusedCount} held (blocked).` : `${head}.`;
}

/** SUB-03 review batch: per-claim select, then Send or Hold; projected charge from perClaimCents. */
const BatchDetail: React.FC<BatchDetailProps> = ({ batchId, onBack }) => {
  const canSubmit = usePermission(BILLING_SUBMIT);
  const [detail, setDetail] = useState<EdiBatchDetail | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await EdiService.getBatch(batchId);
      setDetail(d);
      const held = new Set(d.batch.heldClaimIds);
      setSelected(new Set(d.claims.filter((c) => !held.has(c.claimId) && c.status !== 'SUBMITTED').map((c) => c.claimId)));
    } catch (err) {
      setError(errorText(err, 'Failed to load the batch'));
    }
  }, [batchId]);

  useEffect(() => { void load(); }, [load]);

  const selectedClaims = useMemo(() => (detail?.claims || []).filter((c) => selected.has(c.claimId)), [detail, selected]);
  const chargeableSelected = selectedClaims.filter((c) => c.chargeable).length;
  const chargeText = detail ? projectedChargeText(chargeableSelected, detail.perClaimCents) : null;

  if (!detail) return error ? <div className="p-3"><ErrorNote message={error} /></div> : <LoadingSpinner label="Loading batch" />;

  const { batch, claims } = detail;
  const held = new Set(batch.heldClaimIds);
  const sendable = canSubmit && SENDABLE.has(batch.status);

  const toggle = (claimId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId); else next.add(claimId);
      return next;
    });
  };

  const run = async (kind: 'send' | 'hold') => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (kind === 'send') {
        const { refused } = await EdiService.sendBatch(batchId, ids);
        setNotice(sendNotice(ids.length, refused.length));
      } else {
        await EdiService.holdClaims(batchId, ids);
        setNotice(`${ids.length} ${ids.length === 1 ? 'claim' : 'claims'} held.`);
      }
      await load();
    } catch (err) {
      setError(errorText(err, kind === 'send' ? 'Failed to send the claims' : 'Failed to hold the claims'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 p-3 sm:p-4" aria-label="Batch detail">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft size={14} /> All batches
      </button>
      <div>
        <h2 className="text-base font-semibold text-slate-800">Batch of {formatEdiDate(batch.createdAt)}</h2>
        <p className="text-sm text-slate-600">
          {batch.status.replace(/_/g, ' ').toLowerCase()} · {batch.counts.claims} claims · {batch.mode === 'auto' ? 'automatic' : 'review'}
        </p>
      </div>

      <ErrorNote message={error} />
      {notice && <p role="status" className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}

      <div className="text-sm">
        <div className="hidden grid-cols-12 gap-2 border-b border-slate-100 pb-1 text-xs uppercase text-slate-500 sm:grid">
          <span className="col-span-1" /><span className="col-span-2">Patient</span><span className="col-span-2">Payer · route</span>
          <span className="col-span-2">DOS</span><span className="col-span-1 text-right">Charge</span><span className="col-span-3">Predicted issues</span><span className="col-span-1">Fee</span>
        </div>
        {claims.map((c) => {
          const isHeld = held.has(c.claimId);
          return (
            <div key={c.claimId} className="grid grid-cols-1 gap-1 border-b border-slate-100 py-2 sm:grid-cols-12 sm:gap-2" data-testid={`batch-claim-${c.claimId}`}>
              <div className="sm:col-span-1">
                <input
                  type="checkbox"
                  aria-label={`Select claim for ${c.patientName}`}
                  checked={selected.has(c.claimId)}
                  disabled={!sendable || c.status === 'SUBMITTED'}
                  onChange={() => toggle(c.claimId)}
                  className="h-4 w-4"
                />
                {isHeld && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">Held</span>}
              </div>
              <Cell label="Patient" className="font-medium sm:col-span-2">{c.patientName}</Cell>
              <Cell label="Payer" className="sm:col-span-2">{c.payerName}{c.route ? ` · ${c.route}` : ''}</Cell>
              <Cell label="DOS" className="sm:col-span-2">{formatEdiDate(c.dosFrom)}{c.dosTo && c.dosTo !== c.dosFrom ? `–${formatEdiDate(c.dosTo)}` : ''}</Cell>
              <Cell label="Charge" className="sm:col-span-1 sm:text-right">{formatCents(c.chargeCents)}</Cell>
              <Cell label="Predicted issues" className="sm:col-span-3">
                {c.predictedIssues.length === 0 ? <span className="text-slate-500">None</span> : (
                  <ul className="list-disc pl-4 text-amber-900">{c.predictedIssues.map((p) => <li key={p}>{p}</li>)}</ul>
                )}
              </Cell>
              <Cell label="Fee" className="sm:col-span-1">{c.chargeable ? 'Charged' : 'No fee'}</Cell>
            </div>
          );
        })}
      </div>

      {chargeText && (
        <p className="text-sm text-slate-700" data-testid="projected-charge">Projected charge: {chargeText}</p>
      )}

      {sendable && (
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy || selected.size === 0} onClick={() => run('send')} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50">
            Send {selected.size} selected
          </button>
          <button type="button" disabled={busy || selected.size === 0} onClick={() => run('hold')} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-50">
            Hold {selected.size} selected
          </button>
        </div>
      )}
    </div>
  );
};

export default BatchDetail;
